import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Select, message } from 'antd';

import RichTextEditor from '../RichTextEditor';
import { useCommunityAuth } from '../../../contexts/CommunityAuthContext';
import { usePosts, useMetadata } from '../../../hooks/useCommunity';
import { OverlaySpinner, InlineSpinner } from '../../../componentes/LoadingIndicator';
import { getCreationRestrictionCopy } from '../../../utils/restrictionCopy';
import { extractApiError } from '../../../utils/apiError';

export default function CreatePost({ type = 'blog' }) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { user } = useCommunityAuth();
  const [content, setContent] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    const checkTheme = () => setIsDarkMode(document.documentElement.classList.contains('dark'));
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const { categories, tags, loading: metadataLoading } = useMetadata();
  const categoryOptions = useMemo(
    () =>
      categories.map((category) => ({
        value: category.id,
        label: category.name,
        slug: category.slug || '',
      })),
    [categories]
  );
  const tagOptions = useMemo(
    () =>
      tags.map((tag) => ({
        value: String(tag.id),
        label: tag.name,
        slug: tag.slug || '',
      })),
    [tags]
  );
  const filterByLabelOrSlug = useCallback((inputValue, option) => {
    if (!option) {
      return false;
    }
    const text = `${option.label ?? ''} ${option.slug ?? ''} ${option.value ?? ''}`;
    return text.toLowerCase().includes((inputValue || '').toLowerCase());
  }, []);
  
  const { createPost } = usePosts({}, { skip: true });

  const typeLabel = useMemo(() => (type === 'forum' ? 'Forum' : 'Blog'), [type]);
  const creationRestriction = useMemo(
    () => getCreationRestrictionCopy(user?.status, typeLabel),
    [user?.status, typeLabel]
  );
  const creationBlocked = Boolean(creationRestriction.blocked);

  const handleSave = async (values) => {
    if (!user) {
      message.error('Please login to create a post');
      navigate('/community/login');
      return;
    }
    setIsSubmitting(true);
    try {
      let payload = {
        ...values,
        content,
        post_type: type
      };

      if (type === 'blog' && coverImage) {
        const formData = new FormData();
        Object.keys(payload).forEach(key => {
          if (Array.isArray(payload[key])) {
            payload[key].forEach(val => formData.append(key, val));
          } else if (payload[key] !== undefined && payload[key] !== null) {
            formData.append(key, payload[key]);
          }
        });
        formData.append('cover_image', coverImage);
        payload = formData;
      }

      const post = await createPost(payload);
      const pendingTags = Array.isArray(post?.pending_tags) ? post.pending_tags : [];
      if (pendingTags.length) {
        const label = pendingTags.map((tag) => `"${tag.name}"`).join(', ');
        message.info(
          `Tag${pendingTags.length > 1 ? 's' : ''} ${label} submitted for moderator review.`
        );
      }
      navigate(`/community/${type}/${post.slug}`);
    } catch (err) {
      console.error('Error creating post:', err);
      const errorMessage = extractApiError(err, 'Unable to publish post right now');
      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };



  if (!user) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <div className="bg-gradient-to-br from-red-500/10 to-red-500/5 border border-red-500/30 rounded-xl p-10">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-3xl font-bold text-red-400 mb-4">Authentication Required</h2>
          <p className="text-gray-300 mb-6">You need to be logged in to create posts</p>
          <button 
            onClick={() => navigate('/community/login')}
            className="px-8 py-3 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 rounded-lg text-black font-bold shadow-lg transform hover:scale-105 transition-all duration-300"
          >
            Login Now
          </button>
        </div>
      </div>
    );
  }

  if (creationBlocked) {
    return (
      <div className="max-w-4xl mx-auto text-center py-20">
        <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/40 rounded-xl p-10">
          <div className="text-5xl mb-4">🚫</div>
          <h2 className="text-3xl font-bold text-amber-500 mb-4">{creationRestriction.headline}</h2>
          <p className="text-amber-600 mb-2">{creationRestriction.detail}</p>
          <p className="text-sm text-amber-500/80">
            {creationRestriction.support || 'Contact a community administrator if you believe this is an error.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative" aria-busy={isSubmitting || metadataLoading}>
      <div className="bg-white dark:bg-[#161616] rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden">
        <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-200 dark:border-gray-800">
          <h2 className="blog-title text-xl sm:text-2xl font-bold text-gray-950 dark:text-white">
            Create {typeLabel} Post
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Fill in the details below to publish your {typeLabel.toLowerCase()} post.
          </p>
        </div>

        <div className="px-4 sm:px-6 py-5 sm:py-6">
          <Form
            form={form}
            onFinish={handleSave}
            layout="vertical"
            className="[&_.ant-form-item-label>label]:!text-gray-700 [&_.ant-form-item-label>label]:dark:!text-gray-200 [&_.ant-form-item-label>label]:!font-medium [&_.ant-form-item-label>label]:!text-sm [&_.ant-input]:!rounded-lg [&_.ant-input]:dark:!bg-[#0E0E0E] [&_.ant-input]:dark:!border-gray-800 [&_.ant-input]:dark:!text-white [&_.ant-input]:dark:!placeholder-gray-500 [&_.ant-select-selector]:!rounded-lg [&_.ant-select-selector]:dark:!bg-[#0E0E0E] [&_.ant-select-selector]:dark:!border-gray-800 [&_.ant-select-selection-item]:dark:!text-white [&_.ant-select-selection-placeholder]:dark:!text-gray-500 [&_.ant-form-item-explain]:dark:!text-gray-400 [&_.ant-form-item-extra]:!text-xs [&_.ant-form-item-extra]:dark:!text-gray-500"
            disabled={metadataLoading || isSubmitting}
          >
            <Form.Item name="title" label="Title" rules={[{ required: true, message: 'Please enter a title' }]}>
              <Input placeholder="Enter your post title" />
            </Form.Item>

            {type === 'blog' && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Cover Image <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span></label>
                {!coverImage ? (
                  <label
                    htmlFor="cover-image-input"
                    className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-800 bg-white dark:bg-[#0E0E0E] hover:border-primary dark:hover:border-primary cursor-pointer transition-colors duration-150"
                  >
                    <svg className="w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm text-gray-600 dark:text-gray-300">Choose image</span>
                    <input
                      id="cover-image-input"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setCoverImage(file);
                          setCoverPreview(URL.createObjectURL(file));
                        }
                      }}
                    />
                  </label>
                ) : (
                  <div className="inline-flex items-center gap-3 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-[#0E0E0E]">
                    <img
                      src={coverPreview}
                      alt="Cover"
                      className="w-12 h-12 rounded object-cover flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="text-sm text-gray-700 dark:text-gray-200 truncate max-w-[180px] sm:max-w-xs">{coverImage.name}</div>
                      <div className="text-xs text-gray-400 dark:text-gray-500">{(coverImage.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setCoverImage(null);
                        if (coverPreview) URL.revokeObjectURL(coverPreview);
                        setCoverPreview(null);
                      }}
                      className="ml-1 p-1.5 rounded-md text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                      title="Remove image"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Content</label>
              <div className="rounded-lg">
                <RichTextEditor
                  form={form}
                  isDarkMode={isDarkMode}
                  fieldName="content"
                  value={content}
                  onChange={setContent}
                  placeholder="Start writing something great..."
                  onSave={() => form.submit()}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
              <Form.Item name="category" label="Category" className="!mb-0">
                <Select
                  placeholder="Select category"
                  allowClear
                  className="w-full"
                  showSearch
                  loading={metadataLoading}
                  filterOption={filterByLabelOrSlug}
                  options={categoryOptions}
                />
              </Form.Item>

              <Form.Item
                name="tags"
                label="Tags"
                extra="New tags require moderator approval."
                className="!mb-0"
              >
                <Select
                  mode="tags"
                  placeholder="Add or create tags"
                  className="w-full"
                  popupMatchSelectWidth={false}
                  allowClear
                  showSearch
                  loading={metadataLoading}
                  filterOption={filterByLabelOrSlug}
                  options={tagOptions}
                />
              </Form.Item>
            </div>

            <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3 mt-6 pt-5 border-t border-gray-200 dark:border-gray-800">
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-5 py-2.5 bg-primary hover:bg-[#229bb8] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <InlineSpinner
                    tone="slate"
                    message={`Publishing...`}
                    messageClassName="text-white"
                  />
                ) : (
                  <>Publish {typeLabel}</>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
                className="px-5 py-2.5 border border-gray-300 dark:border-gray-800 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#0E0E0E] hover:bg-gray-50 dark:hover:bg-[#1F1F1F] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </Form>
        </div>
      </div>

      {(metadataLoading || isSubmitting) && (
        <OverlaySpinner
          message={metadataLoading ? `Loading ${typeLabel.toLowerCase()} editor...` : `Publishing ${typeLabel.toLowerCase()}...`}
        />
      )}
    </div>
  );
}
